import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  KnowledgeLinksService,
  knowledgeLinksQueryKey,
  linkScanQueryKey,
  type LinkScanUpdate,
} from "@/services/knowledgebase/KnowledgeLinksService";

// Each batch of documents reports within about a minute.
const SCAN_SILENCE_TIMEOUT_MS = 3 * 60 * 1000;

/**
 * Links found inside a genie's knowledge documents, and the scan that finds
 * them. The scan runs in the background: its progress is written into the
 * query cache by the global document realtime listener, so it keeps showing
 * after the owner leaves the page and comes back.
 */
export const useKnowledgeLinks = (agentId: string | undefined) => {
  const queryClient = useQueryClient();

  const links = useQuery({
    queryKey: knowledgeLinksQueryKey(agentId),
    queryFn: () => KnowledgeLinksService.list(agentId!),
    enabled: !!agentId,
    staleTime: 60 * 1000,
  });

  const { data: scan = null } = useQuery<LinkScanUpdate | null>({
    queryKey: linkScanQueryKey(agentId),
    queryFn: () => null,
    enabled: !!agentId,
    staleTime: Infinity,
  });

  // Safety net for a missed final broadcast: a scan that has gone quiet is over.
  useEffect(() => {
    if (scan?.status !== "in_progress") return;
    const timer = setTimeout(() => {
      queryClient.setQueryData(linkScanQueryKey(agentId), null);
      queryClient.invalidateQueries({ queryKey: knowledgeLinksQueryKey(agentId) });
    }, SCAN_SILENCE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [scan, agentId, queryClient]);

  const startScan = useMutation({
    mutationFn: (force: boolean) => KnowledgeLinksService.scan(agentId!, force),
    onSuccess: ({ queued }, force) => {
      if (queued === 0) {
        toast.info(force ? "There are no documents to read yet." : "Every document has already been read.");
        return;
      }
      queryClient.setQueryData<LinkScanUpdate>(linkScanQueryKey(agentId), {
        agent_id: agentId!,
        user_id: "",
        status: "in_progress",
        total: queued,
        scanned: 0,
        found: 0,
      });
      toast.success("Reading your knowledge base", {
        description: "You can carry on working — we'll let you know when the links are ready.",
      });
    },
    onError: (error) => {
      console.error("Starting the knowledge base link scan failed", error);
      toast.error("Couldn't start reading the knowledge base. Please try again.");
    },
  });

  return {
    links: links.data?.links ?? [],
    documents: links.data?.documents ?? null,
    isLoading: links.isLoading,
    scan: scan?.status === "in_progress" ? scan : null,
    startScan: (force = false) => startScan.mutate(force),
    isStarting: startScan.isPending,
  };
};
